import './KpiFunnels.scss';
import { Dispatch, SetStateAction, useState, useMemo, useCallback } from 'react';
import * as R from 'ramda';
import * as uuid from 'uuid';
import {
  MdAdd,
  MdSave,
  MdDelete,
  MdOutlineArrowForward,
  MdDeleteForever,
  MdEdit
} from 'react-icons/md';
import { Button } from '../../Components/Button';
import { Appearance, Placement, Size } from '../../Components/designConstants';
import { IconButton } from '../../Components/IconButton';
import { Dropdown } from '../../Components/Dropdown';
import { FullPageSpinner } from '../../Components/Spinner';
import { CrossChannelLambdaFetch } from '../../utils/fetch-utils';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';
import { KpiFunnel } from './kpi-mapping-types';

const MAX_KPI_FUNNEL_INDEX = 4;

const DropdownSection = {
  KPIs: 'KPIs',
  Metrics: 'Metrics'
} as const;
type DropdownSectionType = (typeof DropdownSection)[keyof typeof DropdownSection];

interface Funnel {
  id: number | string;
  name: string;
  kpis: string[];
  isDefault: boolean;
  isNew?: boolean;
}

interface KpiFunnelsProps {
  funnels: KpiFunnel[] | undefined;
  crossChannelKpis: string[];
  company: string;
  setKpiFunnels: Dispatch<SetStateAction<KpiFunnel[] | undefined>>;
}

const KpiFunnels = ({ funnels, crossChannelKpis, company, setKpiFunnels }: KpiFunnelsProps) => {
  const [newFunnels, setNewFunnels] = useState<Record<string, Funnel>>({});
  const [funnelEdits, setFunnelEdits] = useState<Record<string, Partial<Funnel>>>({});
  const [funnelDeletes, setFunnelDeletes] = useState<Record<string | number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const typedFunnels = funnels as Funnel[] | undefined;

  const combinedFunnels = useMemo(() => {
    const currentFunnels = (typedFunnels || []).filter(funnel => !funnelDeletes[funnel.id]);
    return [...currentFunnels, ...Object.values(newFunnels)];
  }, [typedFunnels, newFunnels, funnelDeletes]);

  const handleEdit = useCallback(
    (funnelId: string | number, edit: Partial<Funnel>, isNew?: boolean) => {
      if (isNew) {
        setNewFunnels(current => ({
          ...current,
          [funnelId]: { ...current[funnelId], ...edit }
        }));
      } else {
        setFunnelEdits(current => ({
          ...current,
          [funnelId]: { ...(current[funnelId] || {}), ...edit }
        }));
      }
    },
    []
  );

  const handleDelete = useCallback((id: string | number, isNew: boolean) => {
    if (isNew) {
      setNewFunnels(curr => {
        const { [id]: _, ...rest } = curr;
        return rest;
      });
    } else {
      setFunnelDeletes(curr => ({ ...curr, [id]: true }));
    }
  }, []);

  const valueToUse = useCallback(
    (funnel: Funnel, editType: string, kpiIndex?: number) => {
      if (editType === 'kpis' && kpiIndex !== undefined) {
        const path = funnelEdits[funnel.id]?.[editType];
        if (path?.length && path[kpiIndex] !== undefined) {
          return path[kpiIndex];
        }
        return funnel?.kpis?.[kpiIndex];
      }

      const editsForFunnelId = funnelEdits[funnel.id];
      if (editsForFunnelId && editType in editsForFunnelId) {
        return editsForFunnelId[editType];
      }
      return funnel[editType];
    },
    [funnelEdits]
  );

  const addNewFunnel = useCallback(() => {
    const id = uuid.v4();
    const init: Funnel = {
      id,
      name: '',
      kpis: [],
      isDefault: false,
      isNew: true
    };
    setNewFunnels(current => ({ ...current, [id]: init }));
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const mergedEditedFunnels: Funnel[] = [];
      for (const funnel of typedFunnels || []) {
        if (funnelEdits[funnel.id]) {
          mergedEditedFunnels.push({ ...funnel, ...funnelEdits[funnel.id] });
        }
      }

      await CrossChannelLambdaFetch('/setKpiFunnels', {
        method: 'POST',
        body: {
          company,
          newFunnelPresets: Object.values(newFunnels),
          updatedFunnelPresets: mergedEditedFunnels,
          funnelDeletes: Object.keys(funnelDeletes)
        }
      });
      setSaving(false);
      setFunnelEdits({});
      setNewFunnels({});
      setKpiFunnels(undefined);
      setEditMode(false);
    } catch (e) {
      setSaving(false);
      console.error('Error saving KPI funnels', e);
    }
  }, [company, funnelEdits, typedFunnels, newFunnels, funnelDeletes, setKpiFunnels]);

  const handleDiscardEdits = () => {
    setFunnelEdits({});
    setNewFunnels({});
    setFunnelDeletes({});
    setEditMode(false);
  };

  const dropdownOptions = useMemo(() => {
    const options: { label: string; value: string; section: DropdownSectionType }[] =
      crossChannelKpis.map(kpi => ({
        label: kpi,
        value: kpi,
        section: DropdownSection.KPIs
      }));
    options.push({ label: 'Clicks', value: 'Clicks', section: DropdownSection.Metrics });
    options.push({ label: 'Impressions', value: 'Impressions', section: DropdownSection.Metrics });
    return options;
  }, [crossChannelKpis]);

  const isNOC = useAppSelector(UserRedux.roleSelector) === 3;

  if (!typedFunnels) return <FullPageSpinner />;

  return (
    <div className='kpi-funnel-config' data-testid='kpi-funnels'>
      <div className='kpi-funnel-top-controls'>
        <div className='title'>
          Define funnels to be used for the KPI Funnel chart on the Cross-Channel page
        </div>
        {editMode ? (
          <Button
            size={Size.LG}
            className='funnel-edit-button'
            leadingElement={<MdAdd />}
            onClick={addNewFunnel}
          >
            Add Funnel
          </Button>
        ) : (
          <Button
            size={Size.LG}
            className='funnel-edit-button'
            leadingElement={<MdEdit />}
            onClick={() => setEditMode(!editMode)}
            disabled={isNOC}
          >
            Edit
          </Button>
        )}
      </div>
      <table className='kpi-funnel-table'>
        <thead>
          <tr>
            <th aria-label='Actions' />
            <th className='table-header'>Funnel Name</th>
            <th className='table-header'>KPI 1</th>
            <th className='table-header'>KPI 2</th>
            <th className='table-header'>KPI 3</th>
            <th className='table-header'>KPI 4</th>
            <th className='table-header'>KPI 5</th>
          </tr>
        </thead>
        <tbody>
          {combinedFunnels.map(funnel => {
            const { id, kpis, isNew } = funnel;
            return (
              <tr key={id} className='table-row'>
                <td>
                  {editMode && (
                    <IconButton
                      appearance={Appearance.EMPTY}
                      size={Size.SM}
                      onClick={() => handleDelete(id, Boolean(isNew))}
                    >
                      <MdDeleteForever style={{ color: 'red' }} />
                    </IconButton>
                  )}
                </td>
                <td>
                  <input
                    aria-label='Funnel name'
                    value={valueToUse(funnel, 'name') as string}
                    disabled={!editMode}
                    onChange={e => {
                      const edit = { name: e.target.value };
                      handleEdit(id, edit, isNew);
                    }}
                  />
                </td>
                {[0, 1, 2, 3, 4].map(index => (
                  <td key={index}>
                    <div className='kpi-dropdown-cell'>
                      <Dropdown
                        overlayPlacement={Placement.RIGHT}
                        size={Size.LG}
                        className='kpi-funnel-select-dropdown'
                        disabled={!editMode}
                        value={valueToUse(funnel, 'kpis', index) || ''}
                        section={[DropdownSection.KPIs, DropdownSection.Metrics]}
                        options={dropdownOptions}
                        onChange={newVal => {
                          const kpisCopy = [...(funnelEdits[id]?.kpis || kpis)];
                          kpisCopy[index] = newVal;
                          const edit = { kpis: kpisCopy };
                          handleEdit(id, edit, isNew);
                        }}
                        cancelOnClick={() => {
                          const kpisCopy = [...(funnelEdits[id]?.kpis || kpis)];
                          kpisCopy[index] = null as unknown as string;
                          const edit = { kpis: kpisCopy };
                          handleEdit(id, edit, isNew);
                        }}
                        cancelContent='Remove KPI'
                      />
                      {index !== MAX_KPI_FUNNEL_INDEX && (
                        <MdOutlineArrowForward className='right-arrow' />
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className='kpi-funnel-bottom-controls'>
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          leadingElement={<MdDelete className='discard-edits-icon' />}
          onClick={handleDiscardEdits}
          loading={saving}
        >
          Discard Edits
        </Button>
        <Button
          size={Size.LG}
          leadingElement={<MdSave />}
          onClick={handleSave}
          disabled={R.isEmpty(funnelEdits) && R.isEmpty(newFunnels) && R.isEmpty(funnelDeletes)}
          loading={saving}
        >
          Commit
        </Button>
      </div>
    </div>
  );
};

export { KpiFunnels };
